import React, { useState, useEffect } from 'react';
import { Trans } from 'react-i18next';
import {
  Text,
  TextInput,
  Button,
  AnnouncementContainer,
} from 'mintel-react-ui';
import { mintelGray400 } from 'mintel-tokens';

import { StringField } from '../fields';
import PageWrapper from '../PageWrapper';
import { SelectWrapper } from '../InputWrapper';
import { changeLanguage } from '../shared/utils';
import { HELPDESK_EMAIL } from '../shared/constants';
import useTranslation from '../translations';
import styles from './AccessCode.scss';

export type AccessCodeProps = {
  fields: {
    clientCodeChoice?: StringField;
    clientCodeChoices?: SelectOptions;
    clientCode: StringField;
    patterns?: string[];
  };
  errors: string[];
  returnUri: string;
  clientCode?: string;
  contactInfo?: {
    name?: string;
    email?: string;
    tel?: string;
    extraInfo?: string;
  };
  language?: string;
};

const AccessCode = ({
  fields,
  errors,
  returnUri,
  clientCode = undefined,
  contactInfo = undefined,
  language = 'en',
}: AccessCodeProps): JSX.Element => {
  const { t } = useTranslation();
  useEffect(() => {
    changeLanguage(language);
  }, [language]);

  const [clientCodeSelect, setClientCodeSelect] = useState(
    fields.clientCodeChoice?.value || fields.clientCodeChoice?.value === ''
      ? [fields.clientCodeChoice.value]
      : [],
  );
  const [clientCodeText, setClientCodeText] = useState(
    fields.clientCode.value || '',
  );

  const onCancel = () => {
    window.location.pathname = returnUri;
  };

  return (
    <PageWrapper>
      {errors.length > 0 && (
        <AnnouncementContainer
          announcements={errors.map((error) => ({
            mode: 'alert',
            showCloseButton: false,
            label: error,
            key: error,
            children: '',
          }))}
          mode="alert"
          label={errors[0]}
          overflow
          showCloseButton={false}
          onClose={() => {
            // do nothing
          }}
          onIgnore={() => {
            // do nothing
          }}
        />
      )}
      <form method="POST">
        <div className={styles.accessCode}>
          <Text.Heading2>{t('Please enter your client code')}</Text.Heading2>
          <Text.Body2>
            {t(
              'Setting a client code allows your company to allocate the time spent using Mintel sites to the client. Your company has requested that you enter a code before entering Mintel sites.',
            )}
          </Text.Body2>
          <Text.Body2>
            <Trans>
              You will not be able to access any Mintel resources until you have
              entered a valid client code. Once you have set your client code
              you will be taken back to the site you were trying to access. If
              you have any problems setting a client code, please contact our{' '}
              <a href={`mailto:${HELPDESK_EMAIL}`}>Helpdesk</a>.
            </Trans>
          </Text.Body2>
          {clientCode && (
            <Text.Body2>
              {t('Your current client code is')} <b>{clientCode}</b>
            </Text.Body2>
          )}
          {fields.patterns && (
            <div className={styles.helpBlock}>
              <Text.Heading5>{t('Sample client codes')}</Text.Heading5>
              <Text.Body2>
                {t(
                  'These are example client codes that are in the required format',
                )}
              </Text.Body2>
              <ul className={styles.patternList}>
                {fields.patterns.map((pattern) => (
                  <Text.Body2 tagName="li" key={pattern}>
                    {pattern}
                  </Text.Body2>
                ))}
              </ul>
            </div>
          )}
          {contactInfo && (
            <div className={styles.helpBlock}>
              <Text.Heading5>
                {t('Get help to choose a client code')}
              </Text.Heading5>
              <Text.Body2>
                {t(
                  'If you do not know what client code to set, your company has indicated that this person will be able to assist you.',
                )}
              </Text.Body2>
              <div className={styles.contactInfo}>
                <Text.Body3 color={mintelGray400}>{t('Name')}</Text.Body3>
                <Text.Body2>{contactInfo.name}</Text.Body2>
              </div>
              {contactInfo.email && (
                <div className={styles.contactInfo}>
                  <Text.Body3 color={mintelGray400}>
                    {t('Email address')}
                  </Text.Body3>
                  <Text.Body2>{contactInfo.email}</Text.Body2>
                </div>
              )}
              {contactInfo.tel && (
                <div className={styles.contactInfo}>
                  <Text.Body3 color={mintelGray400}>
                    {t('Telephone')}
                  </Text.Body3>
                  <Text.Body2>{contactInfo.tel}</Text.Body2>
                </div>
              )}
              {contactInfo.extraInfo && (
                <div className={styles.contactInfo}>
                  <Text.Body3 color={mintelGray400}>
                    {t('Additional information')}
                  </Text.Body3>
                  {contactInfo.extraInfo.split('\n').map((info) => (
                    <Text.Body2 key={info}>{info}</Text.Body2>
                  ))}
                </div>
              )}
            </div>
          )}
          {fields.clientCodeChoices && (
            <SelectWrapper
              id={fields.clientCodeChoice?.id}
              name={fields.clientCodeChoice?.name || ''}
              options={fields.clientCodeChoices || []}
              selected={clientCodeSelect}
              onSelect={setClientCodeSelect}
              label={t('Select a client code')}
            />
          )}
          <TextInput
            id={fields.clientCode?.id}
            name={fields.clientCode?.name || ''}
            showIcon={false}
            placeholder={t('Write something')}
            label={
              fields.clientCodeChoices
                ? t('OR, enter a client code for this session')
                : t('Enter a client code for this session')
            }
            showLabel
            value={clientCodeText}
            onChange={({ target }: { target: { value: string } }) =>
              setClientCodeText(target.value)
            }
          />
          <div className={styles.buttonRow}>
            {clientCode && (
              <div className={styles.button}>
                <Button.CancelButton secondary fullWidth />
              </div>
            )}
            <div className={styles.button}>
              <Button.ActionButton
                type="submit"
                fullWidth
                handleClick={onCancel}
              >
                {t('Continue')}
              </Button.ActionButton>
            </div>
          </div>
        </div>
      </form>
    </PageWrapper>
  );
};

export default AccessCode;
